// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Edit, Trash2, Mail, MessageSquare, Bell, Eye, TestTube, Send } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";

interface TriggerTemplate {
  id: string;
  name: string;
  type: 'email' | 'sms' | 'push';
  trigger_event: string;
  category: string;
  is_active: boolean;
  subject?: string;
  content: string;
  html_content?: string;
  audience_filters?: any;
  audience_type?: string;
  created_at: string;
  updated_at: string;
}

interface TriggerEvent {
  id: string;
  name: string;
  slug: string;
  category: string;
  description: string;
  is_system: boolean;
  template_count: number;
}

const AllTriggersManagement: React.FC = () => {
  const [triggers, setTriggers] = useState<TriggerTemplate[]>([]);
  const [events, setEvents] = useState<TriggerEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [editingTrigger, setEditingTrigger] = useState<TriggerTemplate | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [testingTrigger, setTestingTrigger] = useState<TriggerTemplate | null>(null);
  const [previewingTrigger, setPreviewingTrigger] = useState<TriggerTemplate | null>(null);
  const [testEmail, setTestEmail] = useState('');
  const [deletingEvent, setDeletingEvent] = useState<string | null>(null);

  // Fetch all triggers and events
  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch events with category information
      const { data: eventsData, error: eventsError } = await supabase
        .from('communication_events')
        .select(`
          *,
          communication_categories!inner(name, slug)
        `)
        .order('name');

      if (eventsError) throw eventsError;

      // Fetch all communication templates
      const { data: templatesData, error: templatesError } = await supabase
        .from('communication_templates')
        .select('*')
        .order('updated_at', { ascending: false });

      if (templatesError) throw templatesError;

      // Map templates to triggers format
      const processedTriggers: TriggerTemplate[] = (templatesData || []).map(template => ({
        id: template.id,
        name: template.name || 'Untitled',
        type: 'email',
        trigger_event: template.event_id || 'manual',
        category: template.category || 'general',
        is_active: template.is_active || false,
        subject: template.subject || '',
        content: template.body_text || '',
        html_content: template.body_html || '',
        audience_filters: template.audience_filters,
        audience_type: template.audience_type || 'user',
        created_at: template.created_at,
        updated_at: template.updated_at
      }));

      // Process events data with accurate template counts
      const processedEvents: TriggerEvent[] = (eventsData || []).map(event => ({
        id: event.id,
        name: event.name,
        slug: event.slug,
        category: event.communication_categories?.name || 'Unknown',
        description: event.description || '',
        is_system: true,
        template_count: processedTriggers.filter(t => t.trigger_event === event.id).length
      }));

      setTriggers(processedTriggers);
      setEvents(processedEvents);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load triggers and events');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();

    // Set up realtime subscription for communication_events
    const channel = supabase
      .channel('trigger-events-changes')
      .on(
        'postgres_changes',
        {
          event: '*', // Listen to all events (INSERT, UPDATE, DELETE)
          schema: 'public',
          table: 'communication_events'
        },
        (payload) => {
          console.log('Trigger event changed:', payload);
          // Refresh data when events change
          fetchData();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  // Filter events based on search and filters
  const filteredEvents = events.filter(event => {
    const matchesSearch = event.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         event.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedEvent === 'all' || event.category.toLowerCase() === selectedEvent.toLowerCase();
    
    return matchesSearch && matchesCategory;
  });

  const handleCreateTrigger = () => {
    setEditingTrigger(null);
    setIsModalOpen(true);
  };

  const handleEditTrigger = (trigger: TriggerTemplate) => {
    setEditingTrigger(trigger);
    setIsModalOpen(true);
  };

  const handleSaveTrigger = async (updatedTrigger: TriggerTemplate) => {
    try {
      const { error } = await supabase
        .from('communication_templates')
        .update({
          name: updatedTrigger.name,
          subject: updatedTrigger.subject,
          audience_type: updatedTrigger.audience_type,
          is_active: updatedTrigger.is_active,
          category: updatedTrigger.category,
        })
        .eq('id', updatedTrigger.id);

      if (error) throw error;

      toast.success('Template updated successfully');
      setIsModalOpen(false);
      setEditingTrigger(null);
      fetchData();
    } catch (error) {
      console.error('Error updating template:', error);
      toast.error('Failed to update template');
    }
  };

  const handleTestTrigger = (trigger: TriggerTemplate) => {
    setTestingTrigger(trigger);
    setTestEmail('');
  };

  const handleSendTest = async () => {
    if (!testingTrigger || !testEmail) return;

    try {
      const { data, error } = await invokeEdgeFunction('send-test-email', {
        body: { 
          templateId: testingTrigger,
          testEmail: testEmail.trim()
        }
      });

      if (error) throw error;

      toast.success("Test email queued successfully!");
      setTestingTrigger(null);
      setTestEmail("");
    } catch (error: any) {
      console.error('Error sending test email:', error);
      toast.error(error.message || "Failed to send test email");
    }
  };

  const handleDeleteEvent = async (eventId: string) => {
    try {
      const { error } = await supabase
        .from('communication_events')
        .delete()
        .eq('id', eventId);

      if (error) throw error;

      toast.success("Event deleted successfully");
      fetchData();
      setDeletingEvent(null);
    } catch (error: any) {
      console.error('Error deleting event:', error);
      toast.error(error.message || "Failed to delete event");
    }
  };

  const handleToggleActive = async (triggerId: string, isActive: boolean) => {
    try {
      // Update in appropriate table based on trigger source
      const trigger = triggers.find(t => t.id === triggerId);
      if (!trigger) return;

      if (trigger.trigger_event === 'manual') {
        const { error } = await supabase
          .from('communication_templates')
          .update({ is_active: isActive })
          .eq('id', triggerId);

        if (error) throw error;
      }

      // Update local state
      setTriggers(prev => prev.map(t => 
        t.id === triggerId ? { ...t, is_active: isActive } : t
      ));

      toast.success(`Trigger ${isActive ? 'enabled' : 'disabled'} successfully`);
    } catch (error) {
      console.error('Error updating trigger:', error);
      toast.error('Failed to update trigger');
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'email': return <Mail className="h-4 w-4" />;
      case 'sms': return <MessageSquare className="h-4 w-4" />;
      case 'push': return <Bell className="h-4 w-4" />;
      default: return <Mail className="h-4 w-4" />;
    }
  };

  const getCategoryColor = (category: string) => {
    const colors = {
      'transactional': 'bg-blue-100 text-blue-800',
      'marketing': 'bg-green-100 text-green-800',
      'system': 'bg-purple-100 text-purple-800',
      'athlete': 'bg-orange-100 text-orange-800',
      'coach': 'bg-indigo-100 text-indigo-800',
      'general': 'bg-gray-100 text-gray-800'
    };
    return colors[category as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="mt-2 text-muted-foreground">Loading all triggers...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">All Communication Triggers</h2>
          <p className="text-muted-foreground">
            Manage all automated and manual communication triggers in one place
          </p>
        </div>
        <Button onClick={handleCreateTrigger}>
          <Plus className="h-4 w-4 mr-2" />
          Create New Trigger
        </Button>
      </div>

      <Tabs defaultValue="triggers" className="space-y-4">
        <TabsList>
          <TabsTrigger value="triggers">All Email Templates ({events.length} events)</TabsTrigger>
          <TabsTrigger value="events">Browse Events ({events.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="triggers" className="space-y-4">
          {/* Filters */}
          <Card>
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="search">Search Events</Label>
                  <Input
                    id="search"
                    placeholder="Search by event name..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="event">Category</Label>
                  <Select value={selectedEvent} onValueChange={setSelectedEvent}>
                    <SelectTrigger>
                      <SelectValue placeholder="All Categories" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Categories</SelectItem>
                      <SelectItem value="transactional">Transactional</SelectItem>
                      <SelectItem value="marketing">Marketing</SelectItem>
                      <SelectItem value="system">System</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end">
                  <Button variant="outline" onClick={fetchData}>
                    Refresh Data
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Events as Trigger Rules with Templates */}
          <div className="space-y-3">
            {filteredEvents.map((event) => {
              const eventTemplates = triggers.filter(t => t.trigger_event === event.id);
              
              return (
                <Card key={event.id}>
                  <CardContent className="py-4">
                    <div className="space-y-4">
                      {/* Event Header */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4 flex-1">
                          {/* Event Trigger */}
                          <div className="flex items-center gap-2 min-w-[200px]">
                            <Badge className={getCategoryColor(event.category)}>
                              {event.category}
                            </Badge>
                            <span className="font-medium">{event.name}</span>
                          </div>

                          {/* Arrow */}
                          <div className="text-muted-foreground">→</div>

                          {/* Action */}
                          <div className="flex items-center gap-2">
                            <Mail className="h-4 w-4 text-muted-foreground" />
                            <span className="text-sm text-muted-foreground">
                              Send Email
                            </span>
                          </div>

                          {/* Template Count */}
                          <Badge variant="outline">
                            {event.template_count} {event.template_count === 1 ? 'template' : 'templates'}
                          </Badge>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              // Create new template for this event
                              handleEditTrigger({
                                id: '',
                                name: '',
                                type: 'email',
                                trigger_event: event.id,
                                category: event.category,
                                is_active: true,
                                content: '',
                                created_at: new Date().toISOString(),
                                updated_at: new Date().toISOString()
                              });
                            }}
                          >
                            <Plus className="h-4 w-4 mr-1" />
                            Add Template
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeletingEvent(event.id)}
                            title="Delete event"
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </div>

                      {/* Event Description */}
                      <p className="text-sm text-muted-foreground">
                        {event.description}
                      </p>

                      {/* Associated Templates */}
                      {eventTemplates.length > 0 ? (
                        <div className="space-y-2 pl-4 border-l-2 border-primary/20">
                          {eventTemplates.map((template) => (
                            <div
                              key={template.id}
                              className="flex items-center justify-between p-3 bg-muted/30 rounded-md hover:bg-muted/50 transition-colors"
                            >
                              <div className="flex items-center gap-3 flex-1">
                                <Mail className="h-4 w-4 text-muted-foreground" />
                            <div className="flex-1">
                              <div className="font-medium text-sm">{template.name}</div>
                              {template.subject && (
                                <div className="text-xs text-muted-foreground">
                                  Subject: {template.subject}
                                </div>
                              )}
                              <div className="text-xs text-muted-foreground mt-1">
                                <span className="font-semibold">To:</span> <span className="font-medium">{template.audience_type || 'user'}</span>
                              </div>
                            </div>
                            <Badge variant={template.is_active ? "default" : "secondary"}>
                              {template.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                              </div>
                              <div className="flex items-center gap-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setPreviewingTrigger(template)}
                                  title="Preview email"
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleTestTrigger(template)}
                                  title="Send test email"
                                >
                                  <Send className="h-4 w-4" />
                                </Button>
                                <Switch
                                  checked={template.is_active}
                                  onCheckedChange={(checked) => handleToggleActive(template.id, checked)}
                                />
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleEditTrigger(template)}
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={async () => {
                                    if (confirm('Delete this template?')) {
                                      try {
                                        await supabase
                                          .from('communication_templates')
                                          .delete()
                                          .eq('id', template.id);
                                        toast.success('Template deleted');
                                        fetchData();
                                      } catch (error) {
                                        toast.error('Failed to delete template');
                                      }
                                    }
                                  }}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="pl-4 py-2 text-sm text-muted-foreground italic border-l-2 border-dashed border-muted">
                          No templates added yet. Click "Add Template" to create an email for this event. You'll be able to set the recipient (To: user, admin, coach, or specific email).
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {filteredEvents.length === 0 && (
            <Card>
              <CardContent className="py-8 text-center">
                <p className="text-muted-foreground">No events found matching your search.</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="events" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {events.map((event) => (
              <Card key={event.id}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <h3 className="font-medium">{event.name}</h3>
                    <Badge variant="secondary">{event.template_count} templates</Badge>
                  </div>
                  <Badge className={getCategoryColor(event.category)}>
                    {event.category}
                  </Badge>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-3">
                    {event.description}
                  </p>
                  <div className="flex items-center justify-between">
                    <code className="text-xs bg-muted px-2 py-1 rounded">
                      {event.slug}
                    </code>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSelectedEvent(event.slug);
                        // Switch to triggers tab by updating the URL or using state
                        const triggersTab = document.querySelector('[value="triggers"]') as HTMLButtonElement;
                        if (triggersTab) {
                          triggersTab.click();
                        }
                      }}
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      View
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Test Trigger Dialog */}
      {testingTrigger && (
        <Dialog open={!!testingTrigger} onOpenChange={() => setTestingTrigger(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Send Test Email: {testingTrigger.name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="test-email">Test Email Address</Label>
                <Input
                  id="test-email"
                  type="email"
                  placeholder="your@email.com"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Enter the email address where you want to receive the test
                </p>
              </div>
              <div className="flex gap-2">
                <Button onClick={handleSendTest}>
                  <Send className="h-4 w-4 mr-2" />
                  Send Test
                </Button>
                <Button variant="outline" onClick={() => setTestingTrigger(null)}>
                  Cancel
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Preview Trigger Dialog */}
      {previewingTrigger && (
        <Dialog open={!!previewingTrigger} onOpenChange={() => setPreviewingTrigger(null)}>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-auto">
            <DialogHeader>
              <DialogTitle>Preview: {previewingTrigger.name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="text-sm font-semibold">Subject:</Label>
                <p className="text-sm mt-1">{previewingTrigger.subject || 'No subject'}</p>
              </div>
              <div>
                <Label className="text-sm font-semibold">To:</Label>
                <p className="text-sm mt-1">{previewingTrigger.audience_type || 'user'}</p>
              </div>
              <div>
                <Label className="text-sm font-semibold">Email Content:</Label>
                <div 
                  className="mt-2 p-4 border rounded-md bg-white"
                  dangerouslySetInnerHTML={{ 
                    __html: previewingTrigger.html_content || previewingTrigger.content.replace(/\n/g, '<br/>') 
                  }}
                />
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setPreviewingTrigger(null)}>
                  Close
                </Button>
                <Button onClick={() => {
                  setPreviewingTrigger(null);
                  handleTestTrigger(previewingTrigger);
                }}>
                  <Send className="h-4 w-4 mr-2" />
                  Send Test
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Edit Trigger Dialog */}
      {editingTrigger && isModalOpen && (
        <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Edit Template</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="edit-name">Template Name</Label>
                <Input
                  id="edit-name"
                  value={editingTrigger.name}
                  onChange={(e) => setEditingTrigger({...editingTrigger, name: e.target.value})}
                  placeholder="Enter template name"
                />
              </div>

              <div>
                <Label htmlFor="edit-subject">Email Subject</Label>
                <Input
                  id="edit-subject"
                  value={editingTrigger.subject || ''}
                  onChange={(e) => setEditingTrigger({...editingTrigger, subject: e.target.value})}
                  placeholder="Enter email subject"
                />
              </div>

              <div>
                <Label htmlFor="edit-audience">Send To (Recipient)</Label>
                <Input
                  id="edit-audience"
                  value={editingTrigger.audience_type || 'user'}
                  onChange={(e) => setEditingTrigger({...editingTrigger, audience_type: e.target.value})}
                  placeholder="e.g., user, admin, coach, nancy@zw2.com"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Enter a role (user, admin, coach) or specific email address
                </p>
              </div>

              <div>
                <Label htmlFor="edit-category">Category</Label>
                <Select 
                  value={editingTrigger.category} 
                  onValueChange={(value) => setEditingTrigger({...editingTrigger, category: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="transactional">Transactional</SelectItem>
                    <SelectItem value="marketing">Marketing</SelectItem>
                    <SelectItem value="system">System</SelectItem>
                    <SelectItem value="general">General</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center gap-2">
                <Switch
                  id="edit-active"
                  checked={editingTrigger.is_active}
                  onCheckedChange={(checked) => setEditingTrigger({...editingTrigger, is_active: checked})}
                />
                <Label htmlFor="edit-active">Active</Label>
              </div>

              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => {
                  setIsModalOpen(false);
                  setEditingTrigger(null);
                }}>
                  Cancel
                </Button>
                <Button onClick={() => handleSaveTrigger(editingTrigger)}>
                  Save Changes
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Delete Event Confirmation Dialog */}
      {deletingEvent && (
        <Dialog open={!!deletingEvent} onOpenChange={() => setDeletingEvent(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete Event</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Are you sure you want to delete this event? This will also delete all associated templates.
              </p>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setDeletingEvent(null)}>
                  Cancel
                </Button>
                <Button 
                  variant="destructive" 
                  onClick={() => handleDeleteEvent(deletingEvent)}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Event
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default AllTriggersManagement;